import React, { useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { ErrorRecord, HandleChange } from 'src/hooks/useForm';
import { useAppSelector } from 'src/store/store';
import Row from '@amzn/meridian/row';
import { createSearchRegExp } from 'src/utils/searchUtils';
import Input from '@amzn/meridian/input';
import Checkbox from '@amzn/meridian/checkbox';
import { TableDataRow } from 'src/store/adminConfigPageSlice/siteConfigPageSlice/types';

interface SiteFormFieldsProps {
  formData: TableDataRow;
  errors: ErrorRecord<TableDataRow>;
  handleChange: HandleChange<TableDataRow>;
}
const SiteFormFields = ({ formData, errors, handleChange }: SiteFormFieldsProps) => {
  const { countries, regions, orgs, subOrgs, buildingTypes } = useAppSelector((state) => state.siteConfigPage);

  const [searchQuery, setSearchQuery] = useState();
  const searchRegExp = createSearchRegExp(searchQuery);
  const matchedCountryOptions = countries
    .map((country) => ({ label: country.name, value: country.name }))
    .filter((option) => !searchQuery || searchRegExp.test(option.label));
  const matchedRegionOptions = regions
    .map((region) => ({ label: region.name, value: region.name }))
    .filter((option) => !searchQuery || searchRegExp.test(option.label));
  const matchedOrgOptions = orgs
    .map((org) => ({ label: org.name, value: org.name }))
    .filter((option) => !searchQuery || searchRegExp.test(option.label));
  const matchedSubOrgOptions = subOrgs
    .map((subOrg) => ({ label: subOrg.name, value: subOrg.name }))
    .filter((option) => !searchQuery || searchRegExp.test(option.label));
  const matchedBuildingType = buildingTypes
    .map((building) => ({ label: building.label, value: building.label }))
    .filter((option) => !searchQuery || searchRegExp.test(option.label));

  return (
    <div>
      <Row widths="fill" spacingInset="400 none">
        <Text>Site Name</Text>
        <Input
          type="text"
          placeholder="Enter site name..."
          aria-label="Site Name"
          value={formData.site}
          onChange={(site) => handleChange({ site })}
          error={errors.site?.error}
          errorMessage={errors.site?.errorMessage}
        />
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Country</Text>
        <Select
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Select country..."
          value={formData.country}
          onChange={(country) => handleChange({ country })}
          error={errors.country?.error}
          errorMessage={errors.country?.errorMessage}
        >
          {matchedCountryOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>
      <Row widths="fill" spacingInset={'400 none'}>
        <Text>Region/State/Province</Text>
        <Select
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Select region..."
          value={formData.region}
          onChange={(region) => handleChange({ region })}
          error={errors.region?.error}
          errorMessage={errors.region?.errorMessage}
        >
          {matchedRegionOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Org</Text>
        <Select
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Select org..."
          value={formData.org}
          onChange={(org) => handleChange({ org })}
          error={errors.org?.error}
          errorMessage={errors.org?.errorMessage}
        >
          {matchedOrgOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Sub Org</Text>
        <Select
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Select sub org..."
          value={formData.subOrg}
          onChange={(subOrg) => handleChange({ subOrg })}
          error={errors.subOrg?.error}
          errorMessage={errors.subOrg?.errorMessage}
        >
          {matchedSubOrgOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Building Type</Text>
        <Select
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Select building type..."
          value={formData.buildingType}
          onChange={(buildingType) => handleChange({ buildingType })}
          error={errors.buildingType?.error}
          errorMessage={errors.buildingType?.errorMessage}
        >
          {matchedBuildingType.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Is Active?</Text>
        <Checkbox
          checked={formData.isActive === true}
          onChange={(isActive) => handleChange({ isActive }, (isActive) => !!isActive)}
          aria-label="Is Active?"
        />
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Average Headcount</Text>
        <Input
          type="number"
          aria-label="Average Headcount"
          placeholder="Enter average headcount..."
          value={formData.headcount}
          onChange={(headcount) => handleChange({ headcount })}
          error={errors.headcount?.error}
          errorMessage={errors.headcount?.errorMessage}
        />
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Description</Text>
        <Input
          type="text"
          placeholder="Enter description..."
          aria-label="Description"
          value={formData.description}
          onChange={(description) => handleChange({ description })}
          error={errors.description?.error}
          errorMessage={errors.description?.errorMessage}
        />
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Is participating in Decision Hub pilot?</Text>
        <Checkbox
          data-cy="is-participating-in-dh-pilot-checkbox"
          checked={formData.participatingInDecisionHubPilot === true}
          onChange={(v) => handleChange({ participatingInDecisionHubPilot: v }, (val) => !!val)}
          aria-label="Is participating in Decision Hub pilot?"
        />
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Is participating in AtoZ Notifications pilot?</Text>
        <Checkbox
          data-cy="is-participating-in-atoz-notifications-checkbox"
          checked={formData.atozNotificationsEnabled === true}
          onChange={(v) => handleChange({ atozNotificationsEnabled: v }, (val) => !!val)}
          aria-label="Is participating in AtoZ Notifications pilot?"
        />
      </Row>

      <Row widths="fill" spacingInset="400 none">
        <Text>Is participating in Job Modifications pilot?</Text>
        <Checkbox
          data-cy="is-participating-in-job-modifications-checkbox"
          checked={formData.jobModificationsEnabled === true}
          onChange={(v) => handleChange({ jobModificationsEnabled: v }, (v) => !!v)}
          aria-label="Is participating in Job Modifications pilot?"
        />
      </Row>
    </div>
  );
};

export default SiteFormFields;
